import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-d929f8c2"),n=n(),_popScopeId(),n)
const _hoisted_1 = { id: "mapContainer" }

import { onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { RotatedMarker } from 'leaflet-marker-rotation';
import { imagePointsWithLocalCartesianCoords } from '@/helpers/geometry';
import { ImagePointGeographic, ImagePoint } from '@/models/image';
import { EnumActionTypes as EnumActionTypesLoading } from '@/store/modules/loading';
import { EnumActionTypes as EnumActionTypesGeometry } from '@/store/modules/geometry';
import { EnumActionTypes as EnumActionTypesImagePoints } from '@/store/modules/image-points';
import config from '@/config';


export default /*#__PURE__*/_defineComponent({
  __name: 'Map',
  props: {
    timestampLastResized: null
  },
  setup(__props: any) {

const props = __props as {
  timestampLastResized: number,
}

const store = useStore();



let map: L.Map = {} as L.Map;
let featureGroupImagePoints: L.FeatureGroup = {} as L.FeatureGroup;
let imagePoints: ImagePointGeographic[] = [];
let currentMarker: L.CircleMarker | null;
let viewMarker: RotatedMarker | null = null;

const defaultMarkerStyle: L.CircleMarkerOptions = {
  fillColor: 'orange',
  fillOpacity: 0.8,
  stroke: false,
  radius: 5,
};

const currentMarkerStyle: L.CircleMarkerOptions = {
  ...defaultMarkerStyle,
  fillColor: 'orangered',
};

const getViewIcon = (fov: number, triangleLength = 64, color = 'dodgerblue') => {
  const angle = (fov * Math.PI) / 180;
  const triangleWidth = Math.abs(2 * triangleLength * Math.tan(angle / 2));

  const offset = triangleLength - triangleWidth < 0 ? (triangleWidth / 2) - (triangleLength / 2) : 0;

  const size: L.PointTuple = [triangleLength + 2 * offset, triangleLength];

  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width=${size[0]} height=${size[1]}>
      <defs>
        <linearGradient id="a" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:transparent;stop-opacity:1"/>
          <stop offset="100%" style="stop-color:${color};stop-opacity:1"/>
        </linearGradient>
      </defs>
      <path d="M${(triangleLength / 2) - (triangleWidth / 2) + offset} 0 L${(triangleLength / 2) + (triangleWidth / 2) + offset} 0 L${triangleLength / 2 + offset} ${triangleLength}Z" stroke="colourname" fill="url(#a)"/>
    </svg>
  `;

  const icon = L.divIcon({
    html: svgString,
    className: '',
    iconSize: size,
    iconAnchor: [size[0] / 2, size[1]],
  });

  return icon;
};

const initMap = () => {
  map = L.map('mapContainer');

  map.attributionControl.setPrefix('');

  map.createPane('viewMarkerPane');
  const viewMarkerPane = map.getPane('viewMarkerPane');
  if (!viewMarkerPane) return;
  viewMarkerPane.style.zIndex = '500';

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  featureGroupImagePoints = L.featureGroup().addTo(map);
};

const getImagePoints = async () => {
  const url = 'data/equirectangular/reference.csv';
  return fetch(url)
    .then((response) => response.text())
    .then((csv) => {
      const rows = csv.replace(/\r/g, '').split(/\n/);
      imagePoints = rows.filter((x) => x).map((x) => {
        const data = x.trim().split(',');
        return {
          image: data[0],
          lon: parseFloat(data[2]),
          lat: parseFloat(data[3]),
          alt: parseFloat(data[4]),
          yaw: parseFloat(data[5]),
          pitch: parseFloat(data[6]),
          roll: parseFloat(data[7]),
          links: data[8] ? data[8].split('|') : null,
        } as ImagePointGeographic;
      });

      const imagePointsWithXY = imagePointsWithLocalCartesianCoords(imagePoints, store.getters['geometry/getReferenceGlobal'], config.cartesianSystemProjectionString);

      store.dispatch(`imagePoints/${EnumActionTypesImagePoints.STORE_IMAGE_POINTS}`, imagePointsWithXY);
    });
};

const selectPoint = async (imagePointGeographic: ImagePointGeographic) => {
  if (store.getters['loading/isImageLoading']) return; // todo
  await store.dispatch(`loading/${EnumActionTypesLoading.SET_IMAGE_LOADING}`, true);
  store.dispatch(`geometry/${EnumActionTypesGeometry.SET_IMAGE_POINT}`, imagePointGeographic.image);
};

const addImagePointsToMap = () => {
  imagePoints.forEach((x) => {
    const marker = L.circleMarker([x.lat, x.lon], { ...defaultMarkerStyle, id: x.image } as L.CircleMarkerOptions & {id: string});
    marker.addTo(featureGroupImagePoints);

    marker.on('click', () => {
      selectPoint(x);
    });
  });

  map.fitBounds(featureGroupImagePoints.getBounds());
};

const setCurrentMarker = (imagePoint: ImagePoint) => {
  if (currentMarker) currentMarker.setStyle(defaultMarkerStyle);
  (featureGroupImagePoints.getLayers() as L.CircleMarker[]).forEach((x) => {
    if ((x.options as L.CircleMarkerOptions & {id: string}).id === imagePoint.image) {
      currentMarker = x;
      x.setStyle(currentMarkerStyle);
    }
  });

  if (!viewMarker) {
    viewMarker = new RotatedMarker([imagePoint.lat, imagePoint.lon], {
      icon: getViewIcon(75),
      pane: 'viewMarkerPane',
      interactive: false,
    }).addTo(map);
  } else {
    viewMarker.setLatLng([imagePoint.lat, imagePoint.lon]);
  }

  map.panTo([imagePoint.lat, imagePoint.lon]);
};

watch(() => props.timestampLastResized, () => map.invalidateSize());

watch(() => store.getters['geometry/getImagePoint'], (imagePoint: ImagePoint) => {
  setCurrentMarker(imagePoint);
}, { deep: true });

watch(() => store.getters['geometry/getOrientation'], (orientation: number[]) => {
  const heading = (-orientation[2] * 180) / Math.PI; // swtiched direction (*-1) & converted to degrees
  if (viewMarker) viewMarker.setRotationAngle(heading);
}, { deep: true });

onMounted(async () => {
  initMap();
  await getImagePoints();
  addImagePointsToMap();
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1))
}
}

})